import type { Excursion } from '@/entities/excursion'
import type { Task } from '@/entities/task'
import { blockDetail, blockLabel, type TimeBlock } from '@/entities/time-block'
import type { Block } from '../model/types'

/**
 * Сетка собирается из трёх независимых чтений: домены `schedule` и `excursion`
 * сознательно не знают друг о друге, а закрытое время читается отдельно от дел —
 * склейка везде работа интерфейса. Дела без времени сюда не попадают: они живут
 * списком в своём разделе.
 */
export function toBlocks(tasks: Task[], excursions: Excursion[], timeBlocks: TimeBlock[]): Block[] {
  const fromTasks: Block[] = tasks
    .filter((task) => task.slot !== null)
    .map((task) => ({
      id: `task:${task.id}`,
      kind: 'task' as const,
      entityId: task.id,
      title: task.title,
      slot: task.slot as NonNullable<Task['slot']>,
      ownerIds: task.assigneeIds,
      done: task.done,
      cancelled: false,
      subtitle: task.note || null,
    }))

  const fromExcursions: Block[] = excursions.map((excursion) => ({
    id: `excursion:${excursion.id}`,
    kind: 'excursion' as const,
    entityId: excursion.id,
    title: excursion.title,
    slot: excursion.slot,
    ownerIds: excursion.guideIds,
    done: excursion.state === 'held',
    // Отменённый выход остаётся в сетке зачёркнутым — иначе непонятно, почему
    // день внезапно пуст.
    cancelled: excursion.state === 'cancelled',
    subtitle: excursion.meetingPoint,
  }))

  // Закрытое время ничего не поручает: отметки выполнения у него нет, а вместо
  // названия — причина словами команды.
  const fromTimeBlocks: Block[] = timeBlocks.map((block) => ({
    id: `time-block:${block.id}`,
    kind: 'time-block' as const,
    entityId: block.id,
    title: blockLabel(block),
    slot: block.slot,
    ownerIds: [block.employeeId],
    done: false,
    cancelled: false,
    subtitle: blockDetail(block),
  }))

  return [...fromExcursions, ...fromTimeBlocks, ...fromTasks]
}
